import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  runDecisionEngine,
  listDecisionRuns,
  getDecisionRun,
  compareVariants,
  diffRuns,
} from "@/lib/decision-engine.functions";
import { PortalShell } from "@/components/portal/PortalShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, ChevronDown, Info, ArrowRight } from "lucide-react";
import { ProjectAnalysisPanel } from "@/components/portal/ProjectAnalysisPanel";
import { DecisionRunStatus, mutationStatus } from "@/components/portal/DecisionRunStatus";

export const Route = createFileRoute("/_authenticated/app/decision/$projectId")({
  component: DecisionCenter,
});

const NAV = [
  { to: "/app/portal", label: "Übersicht" },
  { to: "/app/crm", label: "CRM" },
  { to: "/app/governance", label: "Governance" },
];

function trustVariant(t?: string | null) {
  if (!t) return "outline" as const;
  return t === "A" || t === "B" ? "secondary" : t === "E" ? "destructive" : "outline";
}
function riskVariant(r?: string | null) {
  if (!r) return "outline" as const;
  return r === "low" ? "secondary" : r === "critical" || r === "high" ? "destructive" : "outline";
}
function fmtEur(v: number | string | null | undefined) {
  if (v == null) return "–";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(v));
}
function fmtVal(field: string, v: unknown) {
  if (v == null) return "–";
  if (field.endsWith("_eur")) return fmtEur(v as number);
  if (field.endsWith("_pct")) return `${v} %`;
  return String(v);
}

function readVariantCount(res: any): number | null {
  if (typeof res?.variant_count === "number") return res.variant_count;
  if (Array.isArray(res?.variants)) return res.variants.length;
  return null;
}

function DecisionCenter() {
  const { projectId } = Route.useParams();
  const qc = useQueryClient();
  const listFn = useServerFn(listDecisionRuns);
  const runFn = useServerFn(runDecisionEngine);
  const detailFn = useServerFn(getDecisionRun);
  const compareFn = useServerFn(compareVariants);
  const diffFn = useServerFn(diffRuns);

  const [phase2, setPhase2] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [compareCodes, setCompareCodes] = useState<("A"|"B"|"C"|"D"|"E")[]>(["B","D"]);
  const [diffOther, setDiffOther] = useState<string | null>(null);

  const runs = useQuery({
    queryKey: ["decision-runs", projectId],
    queryFn: () => listFn({ data: { project_id: projectId, limit: 50 } }),
  });

  const activeRunId = selectedRunId ?? runs.data?.runs?.[0]?.id ?? null;

  const detail = useQuery({
    queryKey: ["decision-run", activeRunId],
    queryFn: () => detailFn({ data: { run_id: activeRunId! } }),
    enabled: !!activeRunId,
  });

  const compare = useQuery({
    queryKey: ["decision-compare", activeRunId, compareCodes],
    queryFn: () => compareFn({ data: { run_id: activeRunId!, codes: compareCodes } }),
    enabled: !!activeRunId && compareCodes.length >= 2,
  });

  const diff = useQuery({
    queryKey: ["decision-diff", activeRunId, diffOther],
    queryFn: () => diffFn({ data: { run_a_id: diffOther!, run_b_id: activeRunId! } }),
    enabled: !!activeRunId && !!diffOther && diffOther !== activeRunId,
  });

  const mutation = useMutation({
    mutationFn: () => runFn({ data: { project_id: projectId, phase_type: phase2 ? "phase2" : "phase1", input: {}, trigger_source: "decision_center" } }),
    onSuccess: (res: any) => {
      const runId = res?.run_id ?? res?.id;
      const variants = readVariantCount(res) ?? "—";
      setSelectedRunId(runId);
      qc.invalidateQueries({ queryKey: ["decision-runs", projectId] });
      qc.invalidateQueries({ queryKey: ["decision-run", runId] });
      toast.success("Decision Engine neu ausgeführt", {
        description: `Run-ID ${String(runId).slice(0, 8)}… · ${variants} Varianten · Ergebnisübersicht aktualisiert.`,
      });
    },
    onError: (err: any) => toast.error("Re-Run fehlgeschlagen", { description: err?.message ?? "Unbekannter Fehler" }),
  });

  const scorecardById = useMemo(() => {
    const m: Record<string, any> = {};
    for (const s of detail.data?.scorecards ?? []) m[s.decision_variant_id] = s;
    return m;
  }, [detail.data]);

  return (
    <PortalShell nav={NAV}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Decision Center</h1>
            <p className="text-sm text-muted-foreground">
              5 Pflicht-Varianten · Trust- und Risk-Level getrennt · Shadow-Mode aktiv
            </p>
            <Link to="/app/crm/projects/$id" params={{ id: projectId }} className="text-xs text-primary underline">
              ← Zur Projektakte
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">Shadow-Mode</Badge>
            <div className="flex items-center gap-2 border rounded-md px-2 py-1">
              <Switch id="dc-phase2" checked={phase2} onCheckedChange={setPhase2} />
              <Label htmlFor="dc-phase2" className="text-xs cursor-pointer">Phase 2</Label>
            </div>
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? "Berechne…" : "Decision Engine neu ausführen"}
            </Button>
          </div>
        </div>

        <DecisionRunStatus
          status={mutationStatus(mutation)}
          runId={(mutation.data as any)?.run_id ?? (mutation.data as any)?.id}
          variantCount={readVariantCount(mutation.data)}
          errorMessage={(mutation.error as any)?.message}
          onRetry={mutation.isError ? () => mutation.mutate() : undefined}
        />

        {phase2 && (
          <Card className="border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/10">
            <CardContent className="pt-4 flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span>Phase 2 erfordert Expertenfreigabe und reale Angebots-/Förderdaten. Ergebnisse bleiben intern bis zur Freigabe.</span>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle>Läufe</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {(runs.data?.runs ?? []).map((r: any) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedRunId(r.id)}
                  className={`border rounded-md px-3 py-2 text-left text-sm hover:bg-accent ${
                    activeRunId === r.id ? "border-primary bg-accent" : ""
                  }`}
                >
                  <div className="font-medium">v{r.version} · {r.phase_type}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()} · {r.status} · {r.variant_count ?? 0} Varianten
                  </div>
                  <div className="text-xs mt-1 flex gap-1">
                    <Badge variant={trustVariant(r.aggregated_trust)}>Trust {r.aggregated_trust ?? "–"}</Badge>
                    <Badge variant={riskVariant(r.aggregated_risk)}>Risk {r.aggregated_risk ?? "–"}</Badge>
                  </div>
                </button>
              ))}
              {!runs.data?.runs?.length && (
                <p className="text-sm text-muted-foreground">Noch keine Läufe.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {detail.data && (
          <Tabs defaultValue="variants">
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="variants">Varianten</TabsTrigger>
              <TabsTrigger value="analysis">Analyse</TabsTrigger>
              <TabsTrigger value="compare">Vergleich</TabsTrigger>
              <TabsTrigger value="diff">Diff</TabsTrigger>
              <TabsTrigger value="scorecards">Scorecards</TabsTrigger>
              <TabsTrigger value="recommendations">Empfehlungen</TabsTrigger>
              <TabsTrigger value="answers">Antworten</TabsTrigger>
              <TabsTrigger value="executive">Executive</TabsTrigger>
              <TabsTrigger value="management">Management</TabsTrigger>
              <TabsTrigger value="meta">Repro</TabsTrigger>
            </TabsList>

            <TabsContent value="analysis" className="mt-4">
              <ProjectAnalysisPanel projectId={projectId} />
            </TabsContent>


            <TabsContent value="variants" className="mt-4">
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {detail.data.variants.map((v: any) => {
                  const sc = scorecardById[v.id];
                  return (
                    <Card key={v.id}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center justify-between gap-2">
                          <span>Variante {v.variant_code} · {v.label}</span>
                          {sc && <Badge variant="outline">{sc.score_total}</Badge>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p className="text-muted-foreground text-xs">{v.description}</p>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                          <span className="text-muted-foreground">Investition</span><span className="text-right">{fmtEur(v.investment_eur)}</span>
                          <span className="text-muted-foreground">Förderung</span><span className="text-right">{fmtEur(v.subsidy_eur)}</span>
                          <span className="text-muted-foreground">Jahresvorteil</span><span className="text-right">{fmtEur(v.yearly_savings_eur)}</span>
                          <span className="text-muted-foreground">Autarkie</span><span className="text-right">{v.autarky_pct ?? "–"} %</span>
                          <span className="text-muted-foreground">IRR</span><span className="text-right">{v.irr_pct ?? "–"} %</span>
                          <span className="text-muted-foreground">Payback</span><span className="text-right">{v.payback_years ?? "–"} J</span>
                        </div>
                        <div className="flex flex-wrap gap-1 pt-1">
                          <Badge variant={trustVariant(v.trust_level)}>Trust {v.trust_level}</Badge>
                          <Badge variant={riskVariant(v.risk_level)}>Risk {v.risk_level}</Badge>
                          <Badge variant="outline">{v.phase_type}</Badge>
                        </div>

                        {Array.isArray(v.risk_factors) && v.risk_factors.length > 0 && (
                          <Collapsible>
                            <CollapsibleTrigger className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground">
                              <ChevronDown className="h-3 w-3" /> Risikofaktoren ({v.risk_factors.length})
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <ul className="text-xs text-muted-foreground list-disc pl-4 mt-1 space-y-0.5">
                                {v.risk_factors.map((r: any, i: number) => (
                                  <li key={i}><span className="font-mono">{r.code}</span> · {r.label}</li>
                                ))}
                              </ul>
                            </CollapsibleContent>
                          </Collapsible>
                        )}

                        {sc && (
                          <Sheet>
                            <SheetTrigger asChild>
                              <Button variant="ghost" size="sm" className="w-full h-7 text-xs">
                                <Info className="h-3 w-3 mr-1" /> Score-Erklärung
                              </Button>
                            </SheetTrigger>
                            <SheetContent className="overflow-y-auto">
                              <SheetHeader>
                                <SheetTitle>Score-Erklärung · {v.variant_code}</SheetTitle>
                                <SheetDescription>Gewichtete Summe von 6 Teilbereichen.</SheetDescription>
                              </SheetHeader>
                              <div className="mt-4 space-y-3 text-sm">
                                <div className="grid grid-cols-3 gap-2">
                                  {(["economic","subsidy","tax","autarky","esg","risk"] as const).map((k) => (
                                    <div key={k} className="border rounded p-2 text-center">
                                      <div className="text-xs text-muted-foreground capitalize">{k}</div>
                                      <div className="font-semibold">{(sc as any)[`score_${k}`]}</div>
                                      <div className="text-[10px] text-muted-foreground">w {((sc.weights?.[k] ?? 0) * 100).toFixed(0)}%</div>
                                    </div>
                                  ))}
                                </div>
                                <div className="border-t pt-3">
                                  <div className="text-xs text-muted-foreground">Gesamtscore</div>
                                  <div className="text-2xl font-bold">{sc.score_total} / 100</div>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Formel: {sc.score_explanation?.formula ?? "weighted_sum"} · Ruleset {sc.ruleset_version}
                                </div>
                                <pre className="text-[11px] bg-muted p-2 rounded whitespace-pre-wrap">
{JSON.stringify(sc.score_explanation, null, 2)}
                                </pre>
                              </div>
                            </SheetContent>
                          </Sheet>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="compare" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Variantenvergleich</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {(["A","B","C","D","E"] as const).map((code) => {
                      const active = compareCodes.includes(code);
                      return (
                        <Button
                          key={code}
                          variant={active ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setCompareCodes((prev) =>
                              prev.includes(code)
                                ? prev.filter((x) => x !== code)
                                : [...prev, code],
                            );
                          }}
                        >
                          {code}
                        </Button>
                      );
                    })}
                    <span className="text-xs text-muted-foreground self-center">min. 2, max. 5</span>
                  </div>
                  {compare.data && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead className="text-xs text-muted-foreground">
                          <tr>
                            <th className="text-left p-2">Kennzahl</th>
                            {compare.data.variants.map((v: any) => (
                              <th key={v.id} className="text-right p-2">{v.variant_code}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ["investment_eur","Investition"],
                            ["subsidy_eur","Förderung"],
                            ["yearly_savings_eur","Jahresvorteil"],
                            ["autarky_pct","Autarkie"],
                            ["irr_pct","IRR"],
                            ["payback_years","Payback"],
                            ["trust_level","Trust"],
                            ["risk_level","Risk"],
                          ].map(([f,label]) => (
                            <tr key={f} className="border-t">
                              <td className="p-2 text-muted-foreground">{label}</td>
                              {compare.data!.variants.map((v: any) => (
                                <td key={v.id} className="p-2 text-right">{fmtVal(f, v[f])}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="diff" className="mt-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Diff zwischen Run-Versionen</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Vergleiche</span>
                    <Select value={diffOther ?? ""} onValueChange={(v) => setDiffOther(v || null)}>
                      <SelectTrigger className="w-56"><SelectValue placeholder="Vorherigen Lauf wählen" /></SelectTrigger>
                      <SelectContent>
                        {(runs.data?.runs ?? []).filter((r: any) => r.id !== activeRunId).map((r: any) => (
                          <SelectItem key={r.id} value={r.id}>v{r.version} · {r.phase_type} · {new Date(r.created_at).toLocaleDateString()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">aktueller Lauf</span>
                  </div>
                  {diff.data && (
                    <div className="space-y-3">
                      {diff.data.diffs.map((d: any) => (
                        <div key={d.variant_code} className="border rounded-md p-3">
                          <div className="font-medium text-sm mb-2">Variante {d.variant_code}</div>
                          {d.changes.length === 0 ? (
                            <p className="text-xs text-muted-foreground">Keine Änderungen.</p>
                          ) : (
                            <table className="w-full text-xs">
                              <thead className="text-muted-foreground">
                                <tr><th className="text-left">Feld</th><th className="text-right">vorher</th><th className="text-right">jetzt</th><th className="text-right">Δ</th></tr>
                              </thead>
                              <tbody>
                                {d.changes.map((c: any, i: number) => (
                                  <tr key={i} className="border-t">
                                    <td className="py-1 font-mono">{c.field}</td>
                                    <td className="text-right">{fmtVal(c.field, c.a)}</td>
                                    <td className="text-right">{fmtVal(c.field, c.b)}</td>
                                    <td className={`text-right ${c.delta != null ? (c.delta > 0 ? "text-emerald-600" : c.delta < 0 ? "text-red-600" : "") : "text-muted-foreground"}`}>
                                      {c.delta == null ? "–" : (c.delta > 0 ? "+" : "") + c.delta}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {!diffOther && <p className="text-xs text-muted-foreground">Wähle einen vorherigen Lauf zum Vergleich.</p>}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="scorecards" className="mt-4">
              <Card><CardContent className="pt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr><th className="text-left">Variante</th><th>Eco</th><th>Förd</th><th>Steuer</th><th>Aut</th><th>ESG</th><th>Risiko</th><th>Total</th></tr>
                  </thead>
                  <tbody>
                    {detail.data.scorecards.map((s: any) => {
                      const v = detail.data!.variants.find((x: any) => x.id === s.decision_variant_id);
                      return (
                        <tr key={s.id} className="border-t">
                          <td className="py-2">{v?.variant_code} · {v?.label}</td>
                          <td className="text-center">{s.score_economic}</td>
                          <td className="text-center">{s.score_subsidy}</td>
                          <td className="text-center">{s.score_tax}</td>
                          <td className="text-center">{s.score_autarky}</td>
                          <td className="text-center">{s.score_esg}</td>
                          <td className="text-center">{s.score_risk}</td>
                          <td className="text-center font-semibold">{s.score_total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="recommendations" className="mt-4">
              <div className="grid gap-3 md:grid-cols-2">
                {detail.data.recommendations.map((r: any) => (
                  <Card key={r.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">{r.headline}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <Badge>{r.kind}</Badge>
                        <Badge variant="outline">Variante {r.recommended_variant_code ?? "–"}</Badge>
                        <Badge variant={trustVariant(r.trust_level)}>Trust {r.trust_level}</Badge>
                        <Badge variant={riskVariant(r.risk_level)}>Risk {r.risk_level}</Badge>
                      </div>
                      <p className="text-muted-foreground">{r.recommendation_reason}</p>
                      <details className="text-xs text-muted-foreground">
                        <summary>Quellen & Annahmen</summary>
                        <pre className="whitespace-pre-wrap">{JSON.stringify({ sources: r.recommendation_sources, assumptions: r.recommendation_assumptions }, null, 2)}</pre>
                      </details>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="answers" className="mt-4">
              <Card><CardContent className="pt-6 space-y-2 text-sm">
                {detail.data.answers.map((a: any) => (
                  <div key={a.id} className="flex justify-between border-b py-2">
                    <span className="font-medium">{a.question_key}</span>
                    <span>{a.answer_text}</span>
                  </div>
                ))}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="executive" className="mt-4">
              <Card>
                <CardHeader><CardTitle>Executive Summary</CardTitle></CardHeader>
                <CardContent className="text-sm">
                  {detail.data.executive_summary ? (
                    <div className="grid grid-cols-2 gap-2">
                      <span>Empfohlene Variante</span><span>{detail.data.executive_summary.recommended_variant_code}</span>
                      <span>Investition</span><span>{fmtEur(detail.data.executive_summary.investment_eur)}</span>
                      <span>Förderung</span><span>{fmtEur(detail.data.executive_summary.subsidy_eur)}</span>
                      <span>Steuer</span><span>{fmtEur(detail.data.executive_summary.tax_benefit_eur)}</span>
                      <span>Finanzierung / Monat</span><span>{fmtEur(detail.data.executive_summary.financing_monthly_eur)}</span>
                      <span>Monatsvorteil</span><span>{fmtEur(detail.data.executive_summary.monthly_advantage_eur)}</span>
                      <span>Jahresvorteil</span><span>{fmtEur(detail.data.executive_summary.yearly_advantage_eur)}</span>
                      <span>Autarkie</span><span>{detail.data.executive_summary.autarky_pct ?? "–"} %</span>
                      <span>Rendite</span><span>{detail.data.executive_summary.return_pct ?? "–"} %</span>
                      <span className="col-span-2 mt-2 text-muted-foreground">
                        {detail.data.executive_summary.recommendation_text}
                      </span>
                    </div>
                  ) : <p>Keine Executive Summary.</p>}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="management" className="mt-4">
              <Card>
                <CardHeader><CardTitle>Management Summary</CardTitle></CardHeader>
                <CardContent className="text-sm">
                  {detail.data.management_summary ? (
                    <pre className="whitespace-pre-wrap text-xs bg-muted p-3 rounded-md">
{JSON.stringify(detail.data.management_summary, null, 2)}
                    </pre>
                  ) : <p>Keine Management Summary.</p>}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="meta" className="mt-4">
              <Card>
                <CardHeader><CardTitle>Reproduzierbarkeit</CardTitle></CardHeader>
                <CardContent className="text-sm grid grid-cols-2 gap-2">
                  <span>Engine</span><span>{detail.data.run?.engine_version}</span>
                  <span>Ruleset</span><span>{detail.data.run?.ruleset_version}</span>
                  <span>Prompt</span><span>{detail.data.run?.prompt_version}</span>
                  <span>Input-Hash</span><span className="font-mono text-xs break-all">{detail.data.run?.input_hash}</span>
                  <span>Calculation Timestamp</span><span>{detail.data.run?.calculation_timestamp ?? "–"}</span>
                  <span>Phase</span><span>{detail.data.run?.phase_type}</span>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </PortalShell>
  );
}
